import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Layers } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Input, FieldLabel } from "@/components/ui/Input";
import { Panel } from "@/components/ui/Panel";
import { Select } from "@/components/ui/Select";
import { formatQty } from "@/domain/money";
import {
  MAX_BATCH_CTNS,
  buildCtnBatch,
  formatCtnNo,
  isUntouchedCtn,
  nextCtnNumber,
  parseKg,
  type CtnDraft,
  type CtnItemDraft,
} from "./packingDraft";

export interface BatchLine {
  id: string;
  code: string;
  description: string;
  unit: string;
  remaining: number;
  /** Dòng hàng mẫu cho mỗi thùng — trang dựng giống hệt nút "Thêm vào thùng". */
  item: CtnItemDraft;
}

/**
 * Tạo một nhóm thùng giống nhau (yêu cầu v1.8 mục 7.1): số thùng bắt đầu, số thùng, số lượng mỗi thùng, N.W. / G.W. mỗi thùng và dòng
 * PI. Xem trước dải số và tổng trước khi tạo. Nhóm sau mặc định bắt đầu từ số kế tiếp.
 */
export function BatchCartonsPanel({
  ctns,
  lines,
  measurementOptions,
  onCreate,
}: {
  ctns: CtnDraft[];
  lines: BatchLine[];
  measurementOptions: readonly string[];
  onCreate: (created: CtnDraft[]) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [lineId, setLineId] = useState("");
  const [start, setStart] = useState("");
  const [count, setCount] = useState("");
  const [qty, setQty] = useState("");
  const [nw, setNw] = useState("");
  const [gw, setGw] = useState("");
  const [measurement, setMeasurement] = useState("");
  const [note, setNote] = useState<{ text: string; error: boolean } | null>(null);

  const line = lines.find((l) => l.id === lineId);
  // Thùng mồi trắng trang mở sẵn không tính: nhóm đầu tiên bắt đầu từ 1.
  const nextNo = nextCtnNumber(ctns.filter((c) => !isUntouchedCtn(c)));
  const startValue = start.trim() || String(nextNo);
  const result = line
    ? buildCtnBatch(
        { start: startValue, count, qtyPerCtn: qty, nwKgs: nw, gwKgs: gw, measurementCm: measurement, item: line.item, remaining: line.remaining },
        ctns,
      )
    : null;
  const ready = !!result && !("error" in result);
  const fmtKg = (n: number) => n.toLocaleString("en-US", { minimumFractionDigits: 3, maximumFractionDigits: 3 });

  const create = () => {
    if (!line) return setNote({ text: t("packingBatch.err_line"), error: true });
    if (!result || "error" in result) {
      const code = result && "error" in result ? result.error : "count";
      return setNote({ text: t(`packingBatch.err_${code}`, { max: MAX_BATCH_CTNS, remaining: formatQty(line.remaining) }), error: true });
    }
    onCreate(result.ctns);
    setNote({
      text: t("packingBatch.created", { count: result.ctns.length, from: result.ctns[0]!.ctnNo, to: result.ctns.at(-1)!.ctnNo }),
      error: false,
    });
    // Nhóm tiếp theo: số bắt đầu tự nhảy sau số cuối; giữ dòng hàng để nhập nhóm cân khác cho cùng mã.
    setStart("");
    setCount("");
  };

  if (!open)
    return (
      <Button variant="secondary" size="sm" onClick={() => setOpen(true)}>
        <Layers className="size-3.5" aria-hidden="true" /> {t("packingBatch.open")}
      </Button>
    );

  const preview =
    ready && line
      ? t("packingBatch.preview", {
          from: result.ctns[0]!.ctnNo,
          to: result.ctns.at(-1)!.ctnNo,
          count: result.ctns.length,
          qty: formatQty(result.totalQty),
          unit: line.unit,
          remaining: formatQty(line.remaining),
          nw: fmtKg((parseKg(nw).value ?? 0) * result.ctns.length),
          gw: fmtKg((parseKg(gw).value ?? 0) * result.ctns.length),
        })
      : null;

  return (
    <Panel className="p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-ink">{t("packingBatch.title")}</h3>
          <p className="mt-1 max-w-3xl text-xs text-ink-soft">{t("packingBatch.hint")}</p>
        </div>
        <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
          {t("packingBatch.cancel")}
        </Button>
      </div>
      <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-[minmax(0,2fr)_repeat(5,minmax(0,1fr))]">
        <div>
          <FieldLabel htmlFor="batch-line">{t("packingBatch.line")}</FieldLabel>
          <Select id="batch-line" containerClassName="w-full" value={lineId} onChange={(e) => setLineId(e.target.value)}>
            <option value="">{t("packingBatch.choose")}</option>
            {lines.map((l) => (
              <option key={l.id} value={l.id} disabled={l.remaining <= 0}>
                {t("packingBatch.lineOption", { code: l.code || "—", description: l.description || "—", remaining: formatQty(l.remaining), unit: l.unit })}
              </option>
            ))}
          </Select>
        </div>
        <div>
          <FieldLabel htmlFor="batch-start">{t("packingBatch.start")}</FieldLabel>
          <Input id="batch-start" inputMode="numeric" className="text-right font-mono" placeholder={String(nextNo)} value={start} onChange={(e) => setStart(e.target.value)} />
          <span className="text-2xs text-ink-faint">{formatCtnNo(Number(startValue) || 1)}</span>
        </div>
        <div>
          <FieldLabel htmlFor="batch-count">{t("packingBatch.count")}</FieldLabel>
          <Input id="batch-count" inputMode="numeric" className="text-right font-mono" value={count} onChange={(e) => setCount(e.target.value)} />
        </div>
        <div>
          <FieldLabel htmlFor="batch-qty">{t("packingBatch.qtyPerCtn")}</FieldLabel>
          <Input id="batch-qty" inputMode="numeric" className="text-right font-mono" value={qty} onChange={(e) => setQty(e.target.value)} />
        </div>
        <div>
          <FieldLabel htmlFor="batch-nw">{t("packingBatch.nwPerCtn")}</FieldLabel>
          <Input id="batch-nw" inputMode="decimal" className="text-right font-mono" value={nw} onChange={(e) => setNw(e.target.value)} />
        </div>
        <div>
          <FieldLabel htmlFor="batch-gw">{t("packingBatch.gwPerCtn")}</FieldLabel>
          <Input id="batch-gw" inputMode="decimal" className="text-right font-mono" value={gw} onChange={(e) => setGw(e.target.value)} />
        </div>
      </div>
      <div className="mt-3 grid gap-3 sm:grid-cols-[minmax(0,2fr)_minmax(0,4fr)] sm:items-end">
        <div>
          <FieldLabel htmlFor="batch-measurement">{t("packingBatch.measurement")}</FieldLabel>
          <Input id="batch-measurement" list="batch-sizes" value={measurement} onChange={(e) => setMeasurement(e.target.value)} />
          <datalist id="batch-sizes">{measurementOptions.map((m) => <option key={m} value={m} />)}</datalist>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-3">
          {preview && <p className="font-mono text-xs tabular text-ink-soft">{preview}</p>}
          <Button size="sm" onClick={create}>
            {t("packingBatch.create")}
          </Button>
        </div>
      </div>
      {note && (
        <p role={note.error ? "alert" : "status"} className={`mt-2 text-xs ${note.error ? "text-danger" : "text-ok"}`}>
          {note.text}
        </p>
      )}
    </Panel>
  );
}
